import type { Middleware } from 'instantsearch.js';
import type { FC } from 'react';

import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import { LENGTH, SIZE } from '~/utilities/constants/algolia';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { syncStorageSizeFilter, syncStorageWaistFlag } from '~/utilities/helpers';

export const AlgoliaMiddlewares: FC = () => {
  const { addMiddlewares } = useInstantSearch();
  const { lang } = useAppContext();
  const { visibleFacetsList } = useProductListContext();

  const sizeAttribute = visibleFacetsList.get(SIZE)?.localized ? `${SIZE}.${lang}` : SIZE;
  const lengthAttribute = visibleFacetsList.get(LENGTH)?.localized ? `${LENGTH}.${lang}` : LENGTH;

  const sizeSyncMiddleware: Middleware = useCallback(
    ({ instantSearchInstance }) => ({
      $$type: 'fes.sizeSyncMiddleware',
      onStateChange({ uiState }) {
        try {
          const { indexName } = instantSearchInstance;

          // here we get and process only selected/filtered facet values
          const state = uiState[indexName];
          const size = state?.refinementList?.[sizeAttribute];
          const length = state?.refinementList?.[lengthAttribute];

          syncStorageSizeFilter({ size, length });

          // here we get and process all possible refinements/facets
          const { refinementList } = instantSearchInstance.renderState[indexName];
          const hasSize = Boolean(refinementList?.[sizeAttribute]?.items?.length);
          const hasLength = Boolean(refinementList?.[lengthAttribute]?.items?.length);

          syncStorageWaistFlag({ hasSize, hasLength });
        } catch (e) {
          console.error(e);
        }
      },
    }),
    [sizeAttribute, lengthAttribute]
  );

  const middlewares: Array<Middleware> = useMemo(() => [sizeSyncMiddleware], [sizeSyncMiddleware]);

  useLayoutEffect(() => addMiddlewares(...middlewares), [addMiddlewares, middlewares]);

  return null;
};
